/**
 * @fileoverview Pure helpers for reading/writing the `_db_meta` record and
 *               managing sync checkpoints. Extracted from localDatabaseManager
 *               so they are independently importable and testable.
 * @module local-db/services/dbMetaHelpers
 *
 * Responsibilities:
 * - Read the `_db_meta` record from a Dexie database.
 * - Write the `_db_meta` record.
 * - Build a DbMeta record from expected metadata.
 * - Check if a Dexie database has any data across its tables.
 * - Clear localStorage pull checkpoints for a collection map.
 * - Clear the sync nonce from localStorage.
 * - Check if any migration in a range has `holdWrites: true`.
 *
 * Dependencies:
 * - dexie (Dexie instance type only)
 * - ../types/dbMeta (types only)
 *
 * Architecture:
 * - All functions are pure — they take a Dexie instance or plain arguments
 *   and return values. No closure state, no singletons.
 * - Safe to call during schema upgrades and from the localDatabaseManager.
 *
 * Notes:
 * - These were previously inline module-scoped functions in
 *   localDatabaseManager.ts. Extracting them sharpens the manager's focus on
 *   lifecycle orchestration and makes the helpers independently testable.
 */

import Dexie from 'dexie';
import type { DbMeta, ExpectedDbMeta, MigrationMeta } from '../types/dbMeta';

const DB_META_ID = '_db_meta' as const;

/**
 * @reusable readDbMeta
 *
 * Purpose:
 * - Read the `_db_meta` record from the Dexie database.
 *
 * Returns:
 * - The DbMeta record, or null if the table doesn't exist or the record is
 *   not present (e.g. a pre-manager DB).
 *
 * Rules:
 * - Never throws — table-not-found and read errors return null.
 *
 * Logging:
 * - Not logged; pure I/O helper.
 */
export async function readDbMeta(rawDexie: Dexie): Promise<DbMeta | null> {
  try {
    const table = rawDexie.table('_db_meta');
    const doc = await table.get(DB_META_ID);
    return (doc as DbMeta) ?? null;
  } catch {
    // Table doesn't exist (pre-manager DB) or read error
    return null;
  }
}

/**
 * @reusable writeDbMeta
 *
 * Purpose:
 * - Write the `_db_meta` record to the Dexie database.
 * - Only called by the manager during creation or after recovery.
 *
 * Rules:
 * - Uses `put` (upsert by _id).
 *
 * Logging:
 * - Not logged; pure I/O helper.
 */
export async function writeDbMeta(rawDexie: Dexie, meta: DbMeta): Promise<void> {
  const table = rawDexie.table('_db_meta');
  await table.put(meta);
}

/**
 * @reusable buildDbMeta
 *
 * Purpose:
 * - Build a DbMeta record from expected metadata.
 *
 * Returns:
 * - A new DbMeta object with _id, createdAt, and updatedAt set to `now`.
 *
 * Logging:
 * - Not logged; pure utility.
 */
export function buildDbMeta(expected: ExpectedDbMeta, now: string): DbMeta {
  return {
    _id: DB_META_ID,
    dbId: expected.dbId,
    environment: expected.environment,
    schemaVersion: expected.schemaVersion,
    appVersion: expected.appVersion,
    apiBaseUrl: expected.apiBaseUrl,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * @reusable dbHasData
 *
 * Purpose:
 * - Check if the Dexie database has any data across its tables.
 * - Used to distinguish a fresh DB (no _db_meta, no data) from a pre-manager
 *   DB (no _db_meta, but has data that needs wipe-and-rebuild).
 *
 * Returns:
 * - true if any table has count > 0, false if all tables are empty.
 * - Returns true on read errors (safe default — assume data exists).
 *
 * Logging:
 * - Not logged; pure I/O helper.
 */
export async function dbHasData(rawDexie: Dexie): Promise<boolean> {
  try {
    for (const table of rawDexie.tables) {
      const count = await table.count();
      if (count > 0) return true;
    }
    return false;
  } catch {
    // If we can't read, assume it has data (safe default)
    return true;
  }
}

/**
 * @reusable clearPullCheckpoints
 *
 * Purpose:
 * - Clear all sync pull checkpoints from localStorage for the given
 *   collection map.
 * - Pull checkpoints live in localStorage; push checkpoints live in
 *   _sync_meta (which is wiped when the IndexedDB database is deleted, so
 *   no need to clear them separately here).
 *
 * Rules:
 * - Silently ignores localStorage errors (e.g. in private browsing mode).
 *
 * Logging:
 * - Not logged; pure utility.
 */
export function clearPullCheckpoints(collectionMap: Record<string, string>, checkpointPrefix?: string): void {
  const prefix = checkpointPrefix ? `${checkpointPrefix}_` : '';
  for (const col of Object.keys(collectionMap)) {
    try {
      localStorage.removeItem(`sync_${prefix}pull_${col}`);
      localStorage.removeItem(`sync_${prefix}pull_id_${col}`);
      localStorage.removeItem(`sync_${prefix}pull_id_type_${col}`);
      localStorage.removeItem(`sync_${prefix}fullpull_cursor_${col}`);
    } catch { /* ignore */ }
  }
}

/**
 * @reusable clearSyncNonce
 *
 * Purpose:
 * - Clear the sync nonce from localStorage.
 * - The nonce validates that localStorage checkpoints belong to the current
 *   Dexie instance. After a wipe, the nonce must be cleared so the fresh DB
 *   is not incorrectly validated against a stale nonce.
 *
 * Rules:
 * - Silently ignores localStorage errors.
 *
 * Logging:
 * - Not logged; pure utility.
 */
export function clearSyncNonce(dbName: string, checkpointPrefix?: string): void {
  const prefix = checkpointPrefix ? `${checkpointPrefix}_` : '';
  try {
    localStorage.removeItem(`sync_${prefix}db_nonce_${dbName}`);
  } catch { /* ignore */ }
}

/**
 * @reusable migrationRangeHasHoldWrites
 *
 * Purpose:
 * - Check if any migration in the range (fromVersion → toVersion) has
 *   `holdWrites: true`.
 * - Phase 8 will use this to block writes during major migrations. For
 *   Phase 1, all migrations default to holdWrites: false, so this always
 *   returns false.
 *
 * Returns:
 * - true if any migration in the range has holdWrites: true.
 *
 * Logging:
 * - Not logged; pure utility.
 */
export function migrationRangeHasHoldWrites(
  fromVersion: number,
  toVersion: number,
  registry: Record<number, MigrationMeta>,
): boolean {
  for (let v = fromVersion + 1; v <= toVersion; v++) {
    const meta = registry[v];
    if (meta?.holdWrites) return true;
  }
  return false;
}

/** Re-export the DB_META_ID constant for consumers that need it. */
export { DB_META_ID };
